import { useEffect, useState } from 'react';
import { log } from '../utils/protokoll';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { maengelApi } from '../services/api';
import { MangelWithDetails, MangelStatus } from '../types/maengel';
import { VoiceEnabledTextarea } from '../components/VoiceEnabledTextarea';
import { Fehlermeldung } from './Auth/AuthLayout';
import {
  AlertTriangle,
  Clock,
  CheckCircle,
  AlertCircle,
  Search,
  X,
  Edit2,
  Trash2,
  Plus,
  ArrowRight,
  Calendar,
  MapPin,
  Building2,
  Layers,
  FileText,
  ChevronRight,
  RefreshCw,
  Mic,
  Hammer
} from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import { de } from 'date-fns/locale/de';
import FotoPanel from '../components/FotoPanel';
import { WissensPanel } from '../components/WissensPanel';

/**
 * Schlüssel = Werte des DB-ENUM, label = Anzeigetext.
 *
 * Beides auseinanderzuhalten ist der Kern: bis V414 standen hier
 * deutschsprachige Werte als Schlüssel, und die Datenbank führte teils
 * andere. Was zufällig gleich geschrieben war, funktionierte; was nicht,
 * verschwand still -- `IN_BEARBEITUNG` traf auf kein `In Reparatur`, und
 * jede Zählung offener Mängel war zu niedrig.
 */
type MangelSchweregrad = 'KRITISCH' | 'WICHTIG' | 'NIEDRIG';

const STATUS_CONFIG: Record<MangelStatus, { label: string; color: string; bgColor: string; icon: React.ReactNode }> = {
  'OFFEN': { label: 'Offen', color: 'text-red-700', bgColor: 'bg-red-100', icon: <AlertCircle size={16} /> },
  'IN_BEARBEITUNG': { label: 'In Bearbeitung', color: 'text-amber-700', bgColor: 'bg-amber-100', icon: <Clock size={16} /> },
  'BEHOBEN': { label: 'Behoben', color: 'text-emerald-700', bgColor: 'bg-emerald-100', icon: <CheckCircle size={16} /> },
  'NICHT_BEHOBEN': { label: 'Nicht behoben', color: 'text-red-700', bgColor: 'bg-red-50', icon: <AlertCircle size={16} /> },
  'AKZEPTIERT': { label: 'Nachgeprüft', color: 'text-blue-700', bgColor: 'bg-blue-100', icon: <CheckCircle size={16} /> },
};

// Schluessel = DB-ENUM-Werte, label = Anzeigetext.
const SCHWERE_CONFIG: Record<MangelSchweregrad, { label: string; color: string; bgColor: string; borderColor: string }> = {
  'KRITISCH': { label: 'Kritisch', color: 'text-red-700', bgColor: 'bg-red-100', borderColor: 'border-red-300' },
  'WICHTIG': { label: 'Wichtig', color: 'text-orange-700', bgColor: 'bg-orange-100', borderColor: 'border-orange-300' },
  'NIEDRIG': { label: 'Niedrig', color: 'text-blue-700', bgColor: 'bg-blue-100', borderColor: 'border-blue-300' },
};

/**
 * Erlaubte Statuswechsel.
 *
 * NICHT_BEHOBEN ist das Ergebnis einer Nachprüfung, die den Mangel noch
 * vorfindet -- es folgt deshalb auf BEHOBEN und führt zurück in die
 * Bearbeitung.
 */
const STATUS_TRANSITIONS: Record<MangelStatus, MangelStatus[]> = {
  'OFFEN': ['IN_BEARBEITUNG', 'BEHOBEN'],
  'IN_BEARBEITUNG': ['BEHOBEN', 'OFFEN'],
  'BEHOBEN': ['AKZEPTIERT', 'NICHT_BEHOBEN', 'OFFEN'],
  'NICHT_BEHOBEN': ['IN_BEARBEITUNG', 'OFFEN'],
  'AKZEPTIERT': ['OFFEN'],
};

export default function MaengelPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [schwereFilter, setSchwereFilter] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMangel, setSelectedMangel] = useState<MangelWithDetails | null>(null);
  // Der Anlegen-Dialog existiert nicht: showCreateModal wird nirgends
  // gelesen, der Button "Neuer Mangel" setzt also nur State und bewirkt
  // sonst nichts. Bis der Dialog gebaut ist, bleibt nur der Setter.
  const [, setShowCreateModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusChangeComment, setStatusChangeComment] = useState('');
  const [newStatus, setNewStatus] = useState<MangelStatus | null>(null);
  // Bisher hatten updateStatusMutation/deleteMutation kein onError: ein
  // fehlgeschlagener Statuswechsel/Löschversuch schloss das Modal nicht,
  // aber es erschien auch keinerlei Rückmeldung -- der Nutzer wusste
  // nicht, ob es funktioniert hat oder nicht.
  const [mutationFehler, setMutationFehler] = useState<unknown>(null);
  // Kontextfilter aus Deep-Links (Uebersicht.tsx "Mangel hinzufuegen"
  // bzw. CustomerSettingsPage "Offene Vorgaenge") -- kein einmaliges
  // Aufloesen wie bei ?mangel=, sondern ein aktiver Filter mit eigenem
  // Loeschen-Chip, denn hier soll die gefilterte Liste bestehen bleiben.
  const [elementFilter, setElementFilter] = useState<number | null>(null);
  const [kundeFilter, setKundeFilter] = useState<number | null>(null);

  // Queries
  const { data: maengel, isLoading, refetch } = useQuery<MangelWithDetails[]>({
    queryKey: ['maengel', kundeFilter],
    queryFn: () => maengelApi.getAll(kundeFilter !== null ? { kunde_id: kundeFilter } : undefined),
  });

  const { data: ueberfaellig } = useQuery<MangelWithDetails[]>({
    queryKey: ['maengel-ueberfaellig'],
    queryFn: maengelApi.getUeberfaellig,
  });

  // Deep-Link aus der plattformweiten Suche (?mangel=<id>), analog zum
  // ?element=-Parameter in Uebersicht.tsx.
  useEffect(() => {
    const mangelIdParam = searchParams.get('mangel');
    if (mangelIdParam) {
      const mangelId = parseInt(mangelIdParam);
      if (!isNaN(mangelId)) {
        maengelApi.getById(mangelId).then(setSelectedMangel).catch(log.error);
        setSearchParams({}, { replace: true });
      }
    }
  }, [searchParams, setSearchParams]);

  // Kontextfilter aus Uebersicht.tsx (?element=<id>) und
  // CustomerSettingsPage (?kunde=<id>) -- wird als Filter uebernommen
  // und danach aus der URL entfernt, bleibt aber als State aktiv.
  useEffect(() => {
    const elementIdParam = searchParams.get('element');
    const kundeIdParam = searchParams.get('kunde');
    let geaendert = false;
    if (elementIdParam) {
      const elementId = parseInt(elementIdParam);
      if (!isNaN(elementId)) {
        setElementFilter(elementId);
        geaendert = true;
      }
    }
    if (kundeIdParam) {
      const kundeId = parseInt(kundeIdParam);
      if (!isNaN(kundeId)) {
        setKundeFilter(kundeId);
        geaendert = true;
      }
    }
    if (geaendert) {
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Mutations
  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status, kommentar }: { id: number; status: string; kommentar?: string }) =>
      maengelApi.updateStatus(id, status, kommentar),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maengel'] });
      queryClient.invalidateQueries({ queryKey: ['maengel-ueberfaellig'] });
      setShowStatusModal(false);
      setStatusChangeComment('');
      setNewStatus(null);
      setMutationFehler(null);
      if (selectedMangel) {
        // Refetch selected mangel details
        maengelApi.getById(selectedMangel.id!).then(setSelectedMangel);
      }
    },
    onError: setMutationFehler,
  });

  const deleteMutation = useMutation({
    mutationFn: maengelApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maengel'] });
      setSelectedMangel(null);
      setMutationFehler(null);
    },
    onError: setMutationFehler,
  });

  // Filter logic
  const filteredMaengel = maengel?.filter(m => {
    const matchesElement = elementFilter === null || m.element_id === elementFilter;
    const matchesStatus = !statusFilter || m.status === statusFilter;
    const matchesSchwere = !schwereFilter || m.schweregrad === schwereFilter;
    const matchesSearch = !searchTerm ||
      m.beschreibung?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.element_bezeichnung?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.standort_name?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesElement && matchesStatus && matchesSchwere && matchesSearch;
  });

  // Stats (V2 values)
  const stats = {
    offen: maengel?.filter(m => m.status === 'OFFEN').length || 0,
    ueberfaellig: ueberfaellig?.length || 0,
    kritisch: maengel?.filter(m => m.schweregrad === 'KRITISCH' && m.status !== 'BEHOBEN').length || 0,
    behoben: maengel?.filter(m => m.status === 'BEHOBEN').length || 0,
  };

  const handleStatusChange = (mangel: MangelWithDetails, status: MangelStatus) => {
    setSelectedMangel(mangel);
    setNewStatus(status);
    setShowStatusModal(true);
  };

  const confirmStatusChange = () => {
    if (selectedMangel && newStatus) {
      updateStatusMutation.mutate({
        id: selectedMangel.id!,
        status: newStatus,
        kommentar: statusChangeComment || undefined
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="animate-in fade-in duration-500">
      {mutationFehler != null && (
        <div className="mb-4"><Fehlermeldung fehler={mutationFehler} /></div>
      )}
      {/* Header */}
      <div className="mb-5">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-black tracking-tight text-secondary-900">Mängel-Tracking</h1>
            <p className="text-sm text-secondary-500">
              Übersicht und Nachverfolgung aller Mängel
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => refetch()}
              className="p-3 bg-white hover:bg-secondary-50 border border-secondary-200 rounded-2xl transition-all shadow-sm text-secondary-600"
            >
              <RefreshCw size={20} />
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn btn-primary flex items-center gap-2 shadow-lg shadow-primary-200"
            >
              <Plus size={20} /> Neuer Mangel
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div
            onClick={() => setStatusFilter(statusFilter === 'OFFEN' ? '' : 'OFFEN')}
            className={`p-5 rounded-2xl border transition-all cursor-pointer ${
              statusFilter === 'OFFEN'
                ? 'bg-red-50 border-red-300 shadow-lg'
                : 'bg-white border-secondary-200 hover:border-red-200 hover:shadow-md'
            }`}
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-secondary-900">{stats.offen}</div>
                <div className="text-sm text-secondary-600 font-medium">Offen</div>
              </div>
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                <AlertCircle className="text-red-600" size={24} />
              </div>
            </div>
          </div>

          <div
            className={`p-5 rounded-2xl border transition-all cursor-pointer ${
              stats.ueberfaellig > 0
                ? 'bg-orange-50 border-orange-300'
                : 'bg-white border-secondary-200'
            }`}
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-secondary-900">{stats.ueberfaellig}</div>
                <div className="text-sm text-secondary-600 font-medium">Überfällig</div>
              </div>
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                stats.ueberfaellig > 0 ? 'bg-orange-100' : 'bg-secondary-100'
              }`}>
                <Clock className={stats.ueberfaellig > 0 ? 'text-orange-600' : 'text-secondary-400'} size={24} />
              </div>
            </div>
          </div>

          <div
            onClick={() => setSchwereFilter(schwereFilter === 'KRITISCH' ? '' : 'KRITISCH')}
            className={`p-5 rounded-2xl border transition-all cursor-pointer ${
              schwereFilter === 'KRITISCH'
                ? 'bg-red-50 border-red-300 shadow-lg'
                : 'bg-white border-secondary-200 hover:border-red-200 hover:shadow-md'
            }`}
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-secondary-900">{stats.kritisch}</div>
                <div className="text-sm text-secondary-600 font-medium">Kritisch</div>
              </div>
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                <AlertTriangle className="text-red-600" size={24} />
              </div>
            </div>
          </div>

          <div
            onClick={() => setStatusFilter(statusFilter === 'BEHOBEN' ? '' : 'BEHOBEN')}
            className={`p-5 rounded-2xl border transition-all cursor-pointer ${
              statusFilter === 'BEHOBEN'
                ? 'bg-emerald-50 border-emerald-300 shadow-lg'
                : 'bg-white border-secondary-200 hover:border-emerald-200 hover:shadow-md'
            }`}
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-secondary-900">{stats.behoben}</div>
                <div className="text-sm text-secondary-600 font-medium">Behoben</div>
              </div>
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                <CheckCircle className="text-emerald-600" size={24} />
              </div>
            </div>
          </div>
        </div>

        {/* Kontextfilter aus Deep-Links (Element/Kunde) */}
        {(elementFilter !== null || kundeFilter !== null) && (
          <div className="flex items-center gap-2 mb-4">
            {elementFilter !== null && (
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl text-sm font-medium bg-primary-50 text-primary-700 border border-primary-200">
                Gefiltert nach Element
                <button
                  type="button"
                  onClick={() => setElementFilter(null)}
                  className="hover:text-primary-900"
                  aria-label="Element-Filter entfernen"
                >
                  <X size={14} />
                </button>
              </span>
            )}
            {kundeFilter !== null && (
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl text-sm font-medium bg-primary-50 text-primary-700 border border-primary-200">
                Gefiltert nach Kunde
                <button
                  type="button"
                  onClick={() => setKundeFilter(null)}
                  className="hover:text-primary-900"
                  aria-label="Kunden-Filter entfernen"
                >
                  <X size={14} />
                </button>
              </span>
            )}
          </div>
        )}

        {/* Search and Filters */}
        <div className="flex items-center gap-4">
          <div className="relative flex-1 group">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-secondary-400 group-focus-within:text-primary-500 transition-colors" size={20} />
            <input
              type="text"
              placeholder="Mängel suchen..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 text-sm bg-white border border-secondary-200 rounded-xl focus:ring-2 focus:ring-primary-100 focus:border-primary-500 transition-all outline-none"
            />
          </div>
        </div>

        {/* Filter Pills */}
        <div className="flex flex-wrap gap-4 mt-4">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-secondary-600">Status:</span>
            <div className="flex gap-2">
              {Object.entries(STATUS_CONFIG).map(([status, config]) => (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status === statusFilter ? '' : status)}
                  className={`px-3 py-1.5 text-sm rounded-xl font-medium transition-all ${
                    statusFilter === status
                      ? `${config.bgColor} ${config.color}`
                      : 'bg-secondary-100 text-secondary-600 hover:bg-secondary-200'
                  }`}
                >
                  {config.label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-secondary-600">Schweregrad:</span>
            <div className="flex gap-2">
              {Object.entries(SCHWERE_CONFIG).map(([schwere, config]) => (
                <button
                  key={schwere}
                  onClick={() => setSchwereFilter(schwere === schwereFilter ? '' : schwere)}
                  className={`px-3 py-1.5 text-sm rounded-xl font-medium transition-all ${
                    schwereFilter === schwere
                      ? `${config.bgColor} ${config.color}`
                      : 'bg-secondary-100 text-secondary-600 hover:bg-secondary-200'
                  }`}
                >
                  {config.label}
                </button>
              ))}
            </div>
          </div>
          {(statusFilter || schwereFilter) && (
            <button
              onClick={() => { setStatusFilter(''); setSchwereFilter(''); }}
              className="px-3 py-1.5 text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              Filter zurücksetzen
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Mängel List */}
        <div className="lg:col-span-2 space-y-4">
          {filteredMaengel?.length === 0 ? (
            <div className="py-12 bg-white rounded-2xl border-2 border-dashed border-secondary-200 flex flex-col items-center justify-center text-secondary-400">
              <CheckCircle size={40} className="opacity-20 mb-4" />
              <p className="text-base font-medium">Keine Mängel gefunden</p>
              <p className="text-sm mt-2">
                {statusFilter || schwereFilter ? 'Versuchen Sie andere Filter' : 'Alle Mängel sind behoben!'}
              </p>
            </div>
          ) : (
            filteredMaengel?.map((mangel) => {
              const statusConfig = STATUS_CONFIG[mangel.status as MangelStatus];
              const schwereConfig = SCHWERE_CONFIG[mangel.schweregrad as MangelSchweregrad];
              const isOverdue = mangel.frist_datum && new Date(mangel.frist_datum) < new Date() && mangel.status !== 'BEHOBEN';

              return (
                <div
                  key={mangel.id}
                  onClick={() => setSelectedMangel(mangel)}
                  className={`group p-5 bg-white rounded-2xl border-2 transition-all cursor-pointer ${
                    selectedMangel?.id === mangel.id
                      ? 'border-primary-500 shadow-lg shadow-primary-100 ring-4 ring-primary-50'
                      : `${schwereConfig.borderColor} hover:shadow-lg`
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${schwereConfig.bgColor} ${schwereConfig.color}`}>
                        {schwereConfig.label}
                      </span>
                      <span className={`px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1 ${statusConfig.bgColor} ${statusConfig.color}`}>
                        {statusConfig.icon}
                        {statusConfig.label}
                      </span>
                      {isOverdue && (
                        <span className="px-3 py-1 rounded-full text-xs font-bold bg-orange-100 text-orange-700 flex items-center gap-1">
                          <Clock size={12} /> Überfällig
                        </span>
                      )}
                    </div>
                    {mangel.frist_datum && (
                      <span className="text-sm text-secondary-500 flex items-center gap-1">
                        <Calendar size={14} />
                        {format(new Date(mangel.frist_datum), 'dd.MM.yyyy')}
                      </span>
                    )}
                  </div>

                  <h3 className="font-bold text-base text-secondary-900 mb-2 group-hover:text-primary-600 transition-colors">
                    {mangel.element_bezeichnung || `Element ${mangel.element_id}`}
                  </h3>

                  <p className="text-secondary-600 mb-4 line-clamp-2">{mangel.beschreibung}</p>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 text-xs text-secondary-500">
                      {mangel.standort_name && (
                        <span className="flex items-center gap-1">
                          <MapPin size={12} /> {mangel.standort_name}
                        </span>
                      )}
                      {mangel.kategorie && (
                        <span className="flex items-center gap-1">
                          <FileText size={12} /> {mangel.kategorie}
                        </span>
                      )}
                      {mangel.arbeitsauftrag_id && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/arbeitsauftraege?arbeitsauftrag=${mangel.arbeitsauftrag_id}`);
                          }}
                          className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 font-medium hover:bg-orange-200 transition-colors"
                          title="Zum verknüpften Arbeitsauftrag"
                        >
                          <Hammer size={12} /> Arbeitsauftrag
                        </button>
                      )}
                    </div>
                    <ChevronRight size={20} className="text-secondary-300 group-hover:text-primary-500 transition-colors" />
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Detail Panel */}
        <div className="lg:col-span-1">
          {selectedMangel ? (
            <div className="sticky top-6 p-6 bg-white rounded-2xl border border-secondary-200 shadow-xl animate-in slide-in-from-right-4 duration-300">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-bold text-secondary-900">Mangel-Details</h2>
                <div className="flex gap-2">
                  <button className="p-2 hover:bg-secondary-50 rounded-xl text-secondary-400 hover:text-primary-600 transition-colors">
                    <Edit2 size={18} />
                  </button>
                  <button
                    onClick={() => {
                      if (confirm('Mangel wirklich löschen?')) {
                        deleteMutation.mutate(selectedMangel.id!);
                      }
                    }}
                    className="p-2 hover:bg-red-50 rounded-xl text-secondary-400 hover:text-red-500 transition-colors"
                  >
                    <Trash2 size={18} />
                  </button>
                  <button
                    onClick={() => setSelectedMangel(null)}
                    className="p-2 hover:bg-secondary-50 rounded-xl text-secondary-400 transition-colors"
                  >
                    <X size={18} />
                  </button>
                </div>
              </div>

              {/* Status & Severity */}
              <div className="flex gap-2 mb-6">
                <span className={`px-4 py-2 rounded-xl text-sm font-bold ${
                  SCHWERE_CONFIG[selectedMangel.schweregrad as MangelSchweregrad].bgColor
                } ${SCHWERE_CONFIG[selectedMangel.schweregrad as MangelSchweregrad].color}`}>
                  {SCHWERE_CONFIG[selectedMangel.schweregrad as MangelSchweregrad].label}
                </span>
                <span className={`px-4 py-2 rounded-xl text-sm font-bold flex items-center gap-2 ${
                  STATUS_CONFIG[selectedMangel.status as MangelStatus].bgColor
                } ${STATUS_CONFIG[selectedMangel.status as MangelStatus].color}`}>
                  {STATUS_CONFIG[selectedMangel.status as MangelStatus].icon}
                  {STATUS_CONFIG[selectedMangel.status as MangelStatus].label}
                </span>
              </div>

              {/* Verknuepfter Arbeitsauftrag -- Review-Fund: Mangel.arbeitsauftrag_id
                  war im Backend laengst vorhanden, aber nirgends verlinkt. */}
              {selectedMangel.arbeitsauftrag_id && (
                <button
                  type="button"
                  onClick={() => navigate(`/arbeitsauftraege?arbeitsauftrag=${selectedMangel.arbeitsauftrag_id}`)}
                  className="w-full flex items-center justify-between gap-2 mb-6 p-3 rounded-xl bg-orange-50 border border-orange-200 text-orange-700 hover:bg-orange-100 transition-colors"
                >
                  <span className="flex items-center gap-2 font-medium text-sm">
                    <Hammer size={16} /> Arbeitsauftrag ansehen
                  </span>
                  <ChevronRight size={16} />
                </button>
              )}

              {/* Status Workflow */}
              <div className="mb-6">
                <h4 className="font-bold text-secondary-700 mb-3">Status ändern</h4>
                <div className="flex flex-wrap gap-2">
                  {STATUS_TRANSITIONS[selectedMangel.status as MangelStatus].map((nextStatus) => (
                    <button
                      key={nextStatus}
                      onClick={() => handleStatusChange(selectedMangel, nextStatus)}
                      className={`px-3 py-2 rounded-xl text-sm font-medium flex items-center gap-2 transition-all border ${
                        STATUS_CONFIG[nextStatus].bgColor
                      } ${STATUS_CONFIG[nextStatus].color} hover:shadow-md`}
                    >
                      <ArrowRight size={14} />
                      {STATUS_CONFIG[nextStatus].label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Element Info */}
              <div className="p-4 bg-secondary-50 rounded-2xl mb-6">
                <h4 className="font-bold text-secondary-700 mb-2">Element</h4>
                <p className="font-medium text-secondary-900">{selectedMangel.element_bezeichnung || `Element ${selectedMangel.element_id}`}</p>
                <div className="mt-2 space-y-1 text-sm text-secondary-600">
                  {selectedMangel.standort_name && (
                    <div className="flex items-center gap-2">
                      <MapPin size={14} className="text-secondary-400" />
                      {selectedMangel.standort_name}
                    </div>
                  )}
                  {selectedMangel.gebaeude_name && (
                    <div className="flex items-center gap-2">
                      <Building2 size={14} className="text-secondary-400" />
                      {selectedMangel.gebaeude_name}
                    </div>
                  )}
                  {selectedMangel.stockwerk_name && (
                    <div className="flex items-center gap-2">
                      <Layers size={14} className="text-secondary-400" />
                      {selectedMangel.stockwerk_name}
                    </div>
                  )}
                </div>
              </div>

              {/* Description */}
              <div className="mb-6">
                <h4 className="font-bold text-secondary-700 mb-2">Beschreibung</h4>
                <p className="text-secondary-600">{selectedMangel.beschreibung}</p>
              </div>

              {/* Kategorie */}
              {selectedMangel.kategorie && (
                <div className="mb-6 p-4 bg-blue-50 rounded-2xl border border-blue-100">
                  <h4 className="font-bold text-blue-700 mb-2">Kategorie</h4>
                  <p className="text-blue-600 text-sm">{selectedMangel.kategorie}</p>
                </div>
              )}

              {/*
                * Die Hinweise aus der Wissensdatenbank zu genau dieser
                * Mangelkategorie. Sie erscheinen nur, wenn es welche gibt --
                * ein dauerhaft leerer Kasten wäre Rauschen, und dann fiele
                * auch der Fall nicht mehr auf, in dem etwas darin steht.
                */}
              {selectedMangel.kategorie && (
                <div className="mb-6">
                  <WissensPanel art="MANGELKATEGORIE" ziel={selectedMangel.kategorie} />
                </div>
              )}

              {/* Fotos zum Mangel -- der Beleg, auf den sich die Beschreibung stützt */}
              {selectedMangel.id && (
                <div className="mb-6 pt-4 border-t border-secondary-100">
                  <FotoPanel mangelId={selectedMangel.id} />
                </div>
              )}

              {/* Meta Info */}
              <div className="pt-4 border-t border-secondary-100 space-y-2 text-sm text-secondary-500">
                {selectedMangel.frist_datum && (
                  <div className="flex items-center justify-between">
                    <span>Frist:</span>
                    <span className={`font-medium ${
                      new Date(selectedMangel.frist_datum) < new Date() && selectedMangel.status !== 'BEHOBEN'
                        ? 'text-red-600'
                        : 'text-secondary-900'
                    }`}>
                      {format(new Date(selectedMangel.frist_datum), 'dd.MM.yyyy')}
                    </span>
                  </div>
                )}
                {selectedMangel.erstellt_am && (
                  <div className="flex items-center justify-between">
                    <span>Erstellt:</span>
                    <span className="font-medium text-secondary-900">
                      {formatDistanceToNow(new Date(selectedMangel.erstellt_am), { addSuffix: true, locale: de })}
                    </span>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="sticky top-6 p-10 bg-white rounded-2xl border-2 border-dashed border-secondary-200 flex flex-col items-center justify-center text-secondary-400">
              <AlertTriangle size={36} className="opacity-20 mb-4" />
              <p className="text-sm font-medium">Kein Mangel ausgewählt</p>
              <p className="text-sm mt-1">Klicken Sie auf einen Mangel für Details</p>
            </div>
          )}
        </div>
      </div>

      {/* Status Change Modal */}
      {showStatusModal && selectedMangel && newStatus && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-secondary-900/60 backdrop-blur-sm animate-in zoom-in-95 duration-200">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
            <div className="p-6 border-b border-secondary-100 bg-secondary-50/50">
              <h3 className="text-lg font-bold text-secondary-900">Status ändern</h3>
              <p className="text-sm text-secondary-500 mt-1">
                Von <span className="font-medium">{STATUS_CONFIG[selectedMangel.status as MangelStatus].label}</span> zu{' '}
                <span className="font-medium">{STATUS_CONFIG[newStatus].label}</span>
              </p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-bold text-secondary-700 mb-2 flex items-center gap-2">
                  Kommentar (optional)
                  <span className="text-xs font-normal text-secondary-400 flex items-center gap-1">
                    <Mic size={12} /> Spracheingabe verfügbar
                  </span>
                </label>
                <VoiceEnabledTextarea
                  value={statusChangeComment}
                  onChange={(e) => setStatusChangeComment(e.target.value)}
                  className="input min-h-[100px] resize-none w-full"
                  placeholder="Begründung oder Notizen zur Statusänderung..."
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => { setShowStatusModal(false); setNewStatus(null); setStatusChangeComment(''); }}
                  className="flex-1 btn btn-secondary py-2"
                >
                  Abbrechen
                </button>
                <button
                  onClick={confirmStatusChange}
                  disabled={updateStatusMutation.isPending}
                  className="flex-1 btn btn-primary py-2"
                >
                  {updateStatusMutation.isPending ? 'Wird gespeichert...' : 'Bestätigen'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
